import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting, { ajaxPayroll } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const EMPLOYMENT_TYPES = [
  { value: "permanent", label: "Permanent" },
  { value: "contract",  label: "Contract" },
  { value: "parttime",  label: "Part-Time" },
  { value: "casual",    label: "Casual" },
];

const Payroll = () => {
  const navigate = useNavigate();
  const [tab,       setTab]       = useState("employees"); // employees | runs | process
  const [employees, setEmployees] = useState([]);
  const [runs,      setRuns]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showAdd,   setShowAdd]   = useState(false);
  const [showProcess, setShowProcess] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [processing,setProcessing] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [financialYears, setFinancialYears] = useState([]);

  const [empForm, setEmpForm] = useState({
    employee_number: "", full_name: "", position: "", basic_salary: "",
    bank_name: "", bank_account: "", bank_branch: "", tin: "",
    nssf_number: "", hire_date: "", email: "", phone: "", national_id: "",
    employment_type: null,
  });

  const [runForm, setRunForm] = useState({
    period_month: new Date().getMonth() + 1,
    period_year:  new Date().getFullYear(),
    notes: "",
  });

  // Taking existing users onto the payroll, rather than typing them in again.
  const [showFromUsers, setShowFromUsers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [chosenUsers, setChosenUsers] = useState([]);
  const [addingUsers, setAddingUsers] = useState(false);

  const openFromUsers = async () => {
    setShowFromUsers(true);
    setChosenUsers([]);
    setUserSearch("");
    setUsersLoading(true);
    const res = await ajaxPayroll.availableUsersForPayroll({});
    if (res.status === "OK") setAvailableUsers(res.details || []);
    else toast.error(res.message || "Could not load the staff list.");
    setUsersLoading(false);
  };

  const toggleUser = (id) =>
    setChosenUsers((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const handleAddFromUsers = async () => {
    if (!chosenUsers.length) return toast.error("Choose at least one person.");
    setAddingUsers(true);
    const res = await ajaxPayroll.addEmployeesFromUsers({ user_ids: chosenUsers });
    setAddingUsers(false);
    if (res.status !== "OK") return toast.error(res.message || "Could not add them.");
    toast.success(res.message);
    setShowFromUsers(false);
    fetchEmployees();
  };

  const shownUsers = availableUsers.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return [u.name, u.username, u.email, u.role].some((v) =>
      String(v || "").toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    fetchEmployees();
    fetchRuns();
    ajaxAccounting.listFinancialYears({}).then((r) => {
      if (r.status === "OK") setFinancialYears(r.details || []);
    });
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    const res = await ajaxPayroll.listEmployees({ status: "active" });
    if (res.status === "OK") setEmployees(Array.isArray(res.details) ? res.details : res.details?.list || []);
    setLoading(false);
  };

  const fetchRuns = async () => {
    const res = await ajaxPayroll.listPayrollRuns({});
    if (res.status === "OK") setRuns(Array.isArray(res.details) ? res.details : res.details?.list || []);
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!empForm.employee_number || !empForm.full_name) { toast.error("Employee number and full name are required"); return; }
    setSaving(true);
    const res = await ajaxPayroll.createEmployee({
      ...empForm,
      employment_type: empForm.employment_type?.value || "permanent",
      basic_salary: parseFloat(empForm.basic_salary) || 0,
    });
    if (res.status === "OK") { toast.success("Employee added!"); fetchEmployees(); setShowAdd(false); }
    else toast.error(res.message);
    setSaving(false);
  };

  const payPeriod = () =>
    `${runForm.period_year}-${String(runForm.period_month).padStart(2, "0")}`;
  // Last day of the chosen month — the usual pay date.
  const payDate = () =>
    new Date(runForm.period_year, runForm.period_month, 0).toISOString().slice(0, 10);

  const handlePreview = async () => {
    setProcessing(true);
    const res = await ajaxPayroll.previewPayrollRun({ pay_period: payPeriod() });
    if (res.status === "OK") setPreview(res.details);
    else toast.error(res.message);
    setProcessing(false);
  };

  const handleProcess = async () => {
    const openYear = financialYears.find((y) => y.status === "open") || financialYears[0];
    if (!openYear) { toast.error("Create a financial year before running payroll."); return; }
    setProcessing(true);
    const res = await ajaxPayroll.processPayroll({
      pay_period: payPeriod(),
      pay_date: payDate(),
      financial_year_id: openYear.id,
      notes: runForm.notes,
    });
    if (res.status === "OK") {
      toast.success("Payroll processed successfully!");
      fetchRuns();
      setShowProcess(false);
      setPreview(null);
    } else toast.error(res.message);
    setProcessing(false);
  };

  const handleApprove = async (id) => {
    const res = await ajaxPayroll.approvePayroll({ run_id: id });
    if (res.status === "OK") { toast.success("Payroll approved & posted to ledger!"); fetchRuns(); }
    else toast.error(res.message);
  };

  const handleTerminate = async (emp) => {
    const date   = window.prompt("Termination date (YYYY-MM-DD):", new Date().toISOString().split("T")[0]);
    const reason = window.prompt("Reason for termination:");
    if (!date || !reason) return;
    const res = await ajaxPayroll.terminateEmployee({ id: emp.id, termination_date: date, reason });
    if (res.status === "OK") { toast.success("Employee terminated."); fetchEmployees(); }
    else toast.error(res.message);
  };

  const empColumns = [
    { key: "employee_number", label: "Emp #",      render: (v) => <code>{v}</code> },
    { key: "full_name",        label: "Name",        render: (v) => <strong>{v}</strong> },
    { key: "position",         label: "Position",    render: (v) => v || "—" },
    { key: "employment_type",  label: "Type",        render: (v) => <span className="badge bg-info text-capitalize">{v}</span> },
    { key: "basic_salary",     label: "Basic Salary",render: (v) => functions.formatCurrency(v) },
    { key: "status",           label: "Status",      render: (v) => v === "active" ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span> },
    { key: "id", label: "Actions", render: (_, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/payroll/employee/${row.id}`)}><i className="fa-solid fa-eye"></i></button>
        <RenderSecure code="ACC-PAYROLL">
          {row.status === "active" && <button className="btn btn-sm btn-outline-danger" onClick={() => handleTerminate(row)} title="Terminate"><i className="fa-solid fa-user-minus"></i></button>}
        </RenderSecure>
      </div>
    )},
  ];

  const runColumns = [
    { key: "pay_period",     label: "Period",      render: (v) => <strong>{v}</strong> },
    { key: "employee_count", label: "Employees",   numeric: true, render: (v) => v || "—" },
    { key: "total_gross",    label: "Gross Pay",   numeric: true, render: (v) => functions.formatCurrency(v) },
    { key: "total_paye",     label: "PAYE",        numeric: true, render: (v) => functions.formatCurrency(v) },
    { key: "total_nssf_employee", label: "NSSF",   numeric: true,
      render: (v, r) => functions.formatCurrency((parseFloat(v) || 0) + (parseFloat(r.total_nssf_employer) || 0)) },
    { key: "total_advance_recovered", label: "Advances", numeric: true,
      render: (v) => (parseFloat(v) > 0 ? functions.formatCurrency(v) : "—") },
    { key: "total_net",      label: "Net Pay",     numeric: true, render: (v) => <strong className="tx-success">{functions.formatCurrency(v)}</strong> },
    { key: "status",         label: "Status",      render: (v) => <span className={`badge bg-${v === "approved" ? "success" : "warning"}`}>{v}</span> },
    { key: "id", label: "Actions", render: (id, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/payroll/run/${id}`)}><i className="fa-solid fa-eye"></i></button>
        {row.status === "draft" && (
          <RenderSecure code="ACC-PAYROLL-APPROVE">
            <button className="btn btn-sm btn-success" onClick={() => handleApprove(id)} title="Approve & Post"><i className="fa-solid fa-check"></i></button>
          </RenderSecure>
        )}
      </div>
    )},
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString("en-GB", { month: "long" }),
  }));

  return (
    <AppContainer title="Payroll Management" previousPageTitle="Accounting"
      description="Manage employees and run payroll. Uganda statutory deductions (PAYE, NSSF, LST) are calculated automatically. Approved payroll posts directly to the ledger."
      actions={[
        { label: "Add from Staff",  icon: "fa-solid fa-user-check", className: "btn btn-primary",        onClick: openFromUsers },
        { label: "Add Employee",    icon: "fa-solid fa-user-plus", className: "btn btn-outline-primary", onClick: () => setShowAdd(true) },
        { label: "Run Payroll",     icon: "fa-solid fa-play",      className: "btn btn-outline-primary", onClick: () => setShowProcess(true) },
        { label: "Tax Reports",     icon: "fa-solid fa-file-alt",  className: "btn btn-outline-secondary",onClick: () => navigate("/accounting/tax") },
      ]}>
      <Toaster />

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[{ key: "employees", label: "Employees", icon: "fa-users" }, { key: "runs", label: "Payroll Runs", icon: "fa-calendar-check" }].map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
              {t.key === "employees" && <span className="badge bg-secondary ms-1">{employees.length}</span>}
            </button>
          </li>
        ))}
      </ul>

      {/* EMPLOYEES TAB */}
      {tab === "employees" && (
        <div className="card custom-card p-3">
          <ExportBar data={employees} columns={[
            { key: "employee_number", label: "Emp #" }, { key: "full_name", label: "Name" },
            { key: "position", label: "Position" }, { key: "basic_salary", label: "Basic Salary" }, { key: "status", label: "Status" },
          ]} filename="employees" pdfTitle="Employee List" printCode="ACC-PAYROLL" exportCode="ACC-PAYROLL" />
          <DataTable columns={empColumns} data={employees} loading={loading} pageSize={15} emptyMessage="No employees registered." />
        </div>
      )}

      {/* RUNS TAB */}
      {tab === "runs" && (
        <div className="card custom-card p-3">
          <ExportBar data={runs} columns={[
            { key: "pay_period", label: "Period" }, { key: "total_gross", label: "Gross Pay", numeric: true },
            { key: "total_paye", label: "PAYE" }, { key: "total_nssf", label: "NSSF" },
            { key: "net_pay", label: "Net Pay" }, { key: "status", label: "Status" },
          ]} filename="payroll-runs" pdfTitle="Payroll Runs" printCode="ACC-PAYROLL" exportCode="ACC-PAYROLL" />
          <DataTable columns={runColumns} data={runs} loading={false} pageSize={15} emptyMessage="No payroll runs yet." />
        </div>
      )}

      {/* TAKE EXISTING STAFF ONTO THE PAYROLL */}
      {showFromUsers && (
        <AppModal
          title="Add from staff"
          subtitle="People already on the system who are not yet on the payroll."
          size="lg"
          busy={addingUsers}
          onClose={() => !addingUsers && setShowFromUsers(false)}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowFromUsers(false)} disabled={addingUsers}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAddFromUsers} disabled={addingUsers || !chosenUsers.length}>
                {addingUsers
                  ? "Adding..."
                  : `Add ${chosenUsers.length || ""} ${chosenUsers.length === 1 ? "person" : "people"}`.trim()}
              </button>
            </>
          }
        >
          <input
            className="form-control mb-3"
            placeholder="Search by name, username, email or role"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            autoFocus
          />

          {usersLoading && <p className="tx-13 tx-muted mb-0">Loading staff…</p>}

          {!usersLoading && !availableUsers.length && (
            <p className="tx-13 tx-muted mb-0">
              Everyone on the system is already on the payroll.
            </p>
          )}

          {!usersLoading && availableUsers.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="tx-12 tx-muted">
                  {shownUsers.length} shown · {chosenUsers.length} selected
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() =>
                    setChosenUsers(
                      chosenUsers.length === shownUsers.length ? [] : shownUsers.map((u) => u.user_id)
                    )
                  }
                >
                  {chosenUsers.length === shownUsers.length ? "Clear all" : "Select all shown"}
                </button>
              </div>

              <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #e9ebf1", borderRadius: 10 }}>
                {shownUsers.map((u, i) => (
                  <label
                    key={u.user_id}
                    className="d-flex align-items-center"
                    style={{
                      gap: 12,
                      padding: "10px 14px",
                      borderTop: i ? "1px solid #f2f3f7" : "none",
                      cursor: "pointer",
                      margin: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={chosenUsers.includes(u.user_id)}
                      onChange={() => toggleUser(u.user_id)}
                    />
                    <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                      <span className="d-block fw-semibold tx-13">{u.name}</span>
                      <span className="d-block tx-11 tx-muted">
                        {[u.role, u.email, u.phone].filter((v) => v && v !== "0").join(" · ")}
                      </span>
                    </span>
                  </label>
                ))}
                {!shownUsers.length && (
                  <p className="tx-13 tx-muted m-0 p-3">Nobody matches that search.</p>
                )}
              </div>

              {/* Said plainly, because a wrong salary in a payroll run is worse
                  than an obviously missing one. */}
              <p className="tx-11 tx-muted mt-2 mb-0">
                They join on a zero salary — set each one's pay before the next
                payroll run.
              </p>
            </>
          )}
        </AppModal>
      )}

      {/* ADD EMPLOYEE MODAL */}
      {showAdd && (
        <AppModal
          title="Add Employee"
          size="lg"
          onClose={() => setShowAdd(false)}
          onSubmit={handleAddEmployee}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Add Employee"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Employee Number *</label><input className="form-control" value={empForm.employee_number} onChange={(e) => setEmpForm({...empForm,employee_number:e.target.value})} required /></div>
            <div className="col-lg-8"><label className="form-label tx-12 fw-semibold">Full Name *</label><input className="form-control" value={empForm.full_name} onChange={(e) => setEmpForm({...empForm,full_name:e.target.value})} required /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Position</label><input className="form-control" value={empForm.position} onChange={(e) => setEmpForm({...empForm,position:e.target.value})} /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Employment Type</label><Select options={EMPLOYMENT_TYPES} value={empForm.employment_type} onChange={(v) => setEmpForm({...empForm,employment_type:v})} /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Basic Salary (UGX)</label><input type="number" className="form-control" value={empForm.basic_salary} onChange={(e) => setEmpForm({...empForm,basic_salary:e.target.value})} /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Hire Date</label><input type="date" className="form-control" value={empForm.hire_date} onChange={(e) => setEmpForm({...empForm,hire_date:e.target.value})} /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Email</label><input type="email" className="form-control" value={empForm.email} onChange={(e) => setEmpForm({...empForm,email:e.target.value})} /></div>
            <div className="col-lg-6"><label className="form-label tx-12 fw-semibold">Phone</label><input className="form-control" value={empForm.phone} onChange={(e) => setEmpForm({...empForm,phone:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">TIN</label><input className="form-control" value={empForm.tin} onChange={(e) => setEmpForm({...empForm,tin:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">NSSF Number</label><input className="form-control" value={empForm.nssf_number} onChange={(e) => setEmpForm({...empForm,nssf_number:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">National ID</label><input className="form-control" value={empForm.national_id} onChange={(e) => setEmpForm({...empForm,national_id:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Bank Name</label><input className="form-control" value={empForm.bank_name} onChange={(e) => setEmpForm({...empForm,bank_name:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Bank Account</label><input className="form-control" value={empForm.bank_account} onChange={(e) => setEmpForm({...empForm,bank_account:e.target.value})} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Bank Branch</label><input className="form-control" value={empForm.bank_branch} onChange={(e) => setEmpForm({...empForm,bank_branch:e.target.value})} /></div>
          </div>
        </AppModal>
      )}

      {/* PROCESS PAYROLL MODAL */}
      {showProcess && (
        <AppModal
          title="Run Payroll"
          size="lg"
          onClose={() => setShowProcess(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowProcess(false)}>Cancel</button>
              {preview && (
                <button className="btn btn-primary" onClick={handleProcess} disabled={processing}>
                  {processing ? "Processing..." : <><i className="fa-solid fa-play me-1"></i>Process Payroll</>}
                </button>
              )}
            </>
          }
        >
          <div className="row g-3 mb-3">
            <div className="col-lg-5">
              <label className="form-label tx-12 fw-semibold">Month</label>
              <Select options={monthOptions} value={monthOptions.find((m) => m.value === runForm.period_month)} onChange={(v) => setRunForm({...runForm,period_month:v.value})} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Year</label>
              <input type="number" className="form-control" value={runForm.period_year} min="2020" max="2099" onChange={(e) => setRunForm({...runForm,period_year:parseInt(e.target.value)})} />
            </div>
            <div className="col-lg-3 d-flex align-items-end">
              <button className="btn btn-outline-secondary w-100" onClick={handlePreview} disabled={processing}>{processing ? "Calculating..." : "Preview"}</button>
            </div>
          </div>

          {preview && (
            <div>
              <div className="alert alert-info tx-13 mb-3">
                Payroll preview for <strong>{monthOptions.find((m) => m.value === runForm.period_month)?.label} {runForm.period_year}</strong> — {preview.employee_count} employees
                {preview.totals?.advances_recovered > 0 && (
                  <> · {functions.formatCurrency(preview.totals.advances_recovered)} of staff advances will be recovered</>
                )}
                {preview.already_run && (
                  <div className="mt-1 tx-danger">
                    A run already exists for this period (#{preview.already_run.run_id}, {preview.already_run.status}).
                  </div>
                )}
              </div>
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered">
                  <thead className="bg-light"><tr><th>Employee</th><th>Gross</th><th>PAYE</th><th>NSSF (Emp)</th><th>LST</th><th>Net Pay</th></tr></thead>
                  <tbody>
                    {(preview.payslips || []).map((p, i) => (
                      <tr key={i}>
                        <td>{p.full_name}</td>
                        <td>{functions.formatCurrency(p.gross_pay)}</td>
                        <td className="tx-danger">{functions.formatCurrency(p.paye)}</td>
                        <td className="tx-danger">{functions.formatCurrency(p.nssf_employee)}</td>
                        <td className="tx-danger">{functions.formatCurrency(p.lst)}</td>
                        <td className="tx-success fw-bold">{functions.formatCurrency(p.net_pay)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-light fw-bold">
                    <tr>
                      <td>Totals</td>
                      <td>{functions.formatCurrency(preview.totals?.gross_pay)}</td>
                      <td>{functions.formatCurrency(preview.totals?.paye)}</td>
                      <td>{functions.formatCurrency(preview.totals?.nssf_employee)}</td>
                      <td>{functions.formatCurrency(preview.totals?.lst)}</td>
                      <td className="tx-success">{functions.formatCurrency(preview.totals?.net_pay)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="mb-3">
                <label className="form-label tx-12 fw-semibold">Notes</label>
                <textarea className="form-control" rows={2} value={runForm.notes} onChange={(e) => setRunForm({...runForm,notes:e.target.value})} />
              </div>
            </div>
          )}
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Payroll;
